/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import Modal from "components/Modal";
import genericModalStyles from "helpers/genericModalStyles";
import PropTypes from "prop-types";
import "./_style.scss";
import { useMutation } from "@apollo/client";
import useLanguageProvider from "components/LanguageProvider/hooks/useLanguageProvider";
import LinkButton from "components/LinkButton";
import moment from "moment";
import CREATE_VERIFICATION_CODE from "./queries/createVerificationCode.gql";
import CONFIRM_VERIFICATION_CODE from "./queries/confirmVerificationCode.gql";
import VerificationForm from "./components/VerificationForm";

const ModalPinVerification = ({
  onCloseModal, onVerified, isOpen, email, awaitingCaptcha = false,
}) => {
  const { translate: t } = useLanguageProvider();
  const [verificationKey, setVerificationKey] = useState();
  const [error, setError] = useState();
  const [nextCodeTime, setNextCodeTime] = useState();
  const [countdown, setCountdown] = useState();
  const [canSendCode, setCanSendCode] = useState(false);

  const formContent = [
    {
      fields: [
        {
          type: "text",
          name: "verificationCode",
          label: t("Verification Code"),
          classes: "",
          required: true,
          defaultValue: "",
          validationType: "string",
          validations: [
            {
              type: "required",
              params: [t("Please enter a verification code")],
            },
            {
              type: "min",
              params: [6, t("Verification code must be 6 digits long")],
            },
            {
              type: "max",
              params: [6, t("Verification code must be 6 digits long")],
            },
            {
              type: "matches",
              params: [/\d{6}/, t("Verification code must be 6 digits long")],
            },
          ],
        },
      ],
    },
    {
      fields: [
        {
          type: "submit",
          value: t("Verify"),
          loadingMessage: t("Verifying..."),
          classes: "",
          isSubmitting: loading,
        },
      ],
    },
  ];

  useEffect(() => {
    if (isOpen && email && !verificationKey) {
      generateVerificationCodeFromEmail();
    }
  }, [isOpen, email, verificationKey]);

  const [createVerificationCode] = useMutation(
    CREATE_VERIFICATION_CODE,
    {
      errorPolicy: "none",
      onCompleted(data) {
        if (data.createVerificationCode) {
          setVerificationKey(data.createVerificationCode.key);
          setNextCodeTime(data.createVerificationCode.nextCodeAt);
        } else {
          setError("There was an error sending your verification code. Please try again later.");
        }
      },
      onError(error) {
        if (error.graphQLErrors.length > 0) {
          const firstError = error.graphQLErrors[0];

          if (firstError.extensions?.debugMessage) {
            setError(firstError.extensions?.debugMessage);
          } else {
            setError("There was an error sending your verification code. Please try again later.");
          }
        } else {
          setError("There was an error sending your verification code. Please try again later.");
        }
      },
    },
  );

  const [submitVerificationCode, { loading }] = useMutation(
    CONFIRM_VERIFICATION_CODE,
    {
      errorPolicy: "all",
      onCompleted(data) {
        if (data.confirmVerificationCode.verified) {
          onVerified();
        } else {
          setError("The code you entered was incorrect. Please try again.");
        }
      },
    },
  );

  const generateVerificationCodeFromEmail = () => {
    createVerificationCode({
      variables: {
        input: {
          email,
        },
      },
    });
  };

  const handleSubmit = (data) => {
    submitVerificationCode({
      variables: {
        input: {
          key: email,
          code: data.verificationCode,
        },
      },
    });
  };

  useEffect(() => {
    if (nextCodeTime) {
      const interval = setInterval(() => {
        const nextCodeAt = moment(nextCodeTime);
        const now = moment();
        const diffInSeconds = nextCodeAt.diff(now, "seconds");

        if (diffInSeconds <= 0) {
          setCountdown("0:00");
          setCanSendCode(true);
        } else {
          const minutes = Math.floor(diffInSeconds / 60);
          const seconds = diffInSeconds - (minutes * 60);

          setCountdown(`${minutes}:${seconds}`);
          setCanSendCode(false);
        }
      }, 1000);

      return () => clearInterval(interval);
    }
    setCanSendCode(true);
  }, [nextCodeTime]);

  return (
    <Modal
      contentLabel="Email Verification"
      customStyle={genericModalStyles}
      isOpen={isOpen}
      ariaHideApp={false}
      shouldCloseOnOverlayClick
      onRequestClose={onCloseModal}
      className="pin-verification-modal"
    >
      <div>
        <div className="pin-verification-modal__header">
          <div className="pin-verification-modal__heading">
            Verify your email
          </div>
        </div>

        <p className="pin-verification-model__subtext">
          We have sent a verification code to the email address provided in your complaint.
          Please enter the code from your email below.
        </p>

        <div className="pin-verification-modal__content">
          <VerificationForm formContent={formContent} handleSubmit={handleSubmit} error={error} />
        </div>

        <LinkButton
          className="report-campaign__resend-code"
          onClick={generateVerificationCodeFromEmail}
          look="icon-link"
          isIcon
          size="normal"
          data-test="campaign-report-button"
          disabled={!canSendCode || awaitingCaptcha}
        >
          {canSendCode ? "Didn't get it? Click here to send a new code" : `Resend code in ${countdown}`}
        </LinkButton>
      </div>
    </Modal>
  );
};

ModalPinVerification.propTypes = {
  onCloseModal: PropTypes.func.isRequired,
  onVerified: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  email: PropTypes.string,
};

export default ModalPinVerification;
